import { isMainnet } from 'config'
import { L1ChainId } from 'types'

export enum ChainType {
  Evm = 0,
  Starknet = 1,
}

export enum MainnetChainIds {
  Ethereum = '0x1', // 1
  Optimism = '0xa', // 10
  BSC = '0x38', // 56
  Polygon = '0x89', // 137
  Manta = '0xa9', // 169
  opBNB = '0xcc', // 204
  zkSyncEra = '0x144', // 324
  Metis = '0x440', // 1088
  PolygonZkEVM = '0x44d', // 1101
  Mantle = '0x1388', // 5000
  Base = '0x2105', // 8453
  Arbitrum = '0xa4b1', // 42161
  Avalanche = '0xa86a', // 43114
  Linea = '0xe708', // 59144
  Scroll = '0x82750', // 534352
  Starknet = '0x534e5f4d41494e',
}

export enum TestnetChainIds {
  Goerli = '0x5', // 5
  BSC = '0x61', // 97
  X1 = '0xc3', // 195
  zkSyncEraGoerli = '0x118', // 280
  zkSyncEraSepolia = '0x12c', // 300
  Optimism = '0x1a4', // 420
  PolygonZkEVM = '0x5a2', // 1442
  LightLinkPegasus = '0x763', // 1891
  Avalanche = '0xa869', // 43113
  LineaGoerli = '0xe704', // 59140
  PolygonMumbai = '0x13881', // 80001
  TaikoKatlaL2 = '0x28c60', // 167008
  ArbitrumGoerli = '0x66eed', // 421613
  Scroll = '0x8274f', // 534351
  opBNB = '0x15eb', // 5611
  MantaPacific = '0x34816d', // 3441005
  Sepolia = '0xaa36a7', // 11155111
  StarknetGoerli = '0x534e5f474f45524c49',
  Mantle = '0x1389', // 5001
  Base = '0x14a33', // 84531
  Metis = '0xe9fd',
}

export const BLOCKED_CHAINS: L1ChainId[] = [
  MainnetChainIds.Manta,
  MainnetChainIds.Mantle,
  MainnetChainIds.Metis,
  MainnetChainIds.Scroll,
  MainnetChainIds.PolygonZkEVM,
  MainnetChainIds.Starknet,

  TestnetChainIds.Goerli,
]

export const HOT_CHAINS: L1ChainId[] = [
  TestnetChainIds.zkSyncEraSepolia,
  MainnetChainIds.Optimism,
]
export const NEW_CHAINS: L1ChainId[] = [
  TestnetChainIds.TaikoKatlaL2,
  MainnetChainIds.Ethereum,
]

/**
 * Use to initialize the web3-react connections.
 */
export const RPC_MAPS: Record<L1ChainId, string> = isMainnet
  ? {
      [MainnetChainIds.Ethereum]: 'https://1rpc.io/eth',
      [MainnetChainIds.Optimism]: 'https://1rpc.io/op',
      [MainnetChainIds.BSC]: 'https://1rpc.io/bnb',
      [MainnetChainIds.Polygon]: 'https://1rpc.io/matic',
      [MainnetChainIds.Manta]: 'https://1rpc.io/manta',
      [MainnetChainIds.opBNB]: 'https://opbnb-mainnet-rpc.bnbchain.org',
      [MainnetChainIds.zkSyncEra]: 'https://mainnet.era.zksync.io',
      [MainnetChainIds.Metis]: 'https://andromeda.metis.io/?owner=1088',
      [MainnetChainIds.PolygonZkEVM]: 'https://1rpc.io/polygon/zkevm',
      [MainnetChainIds.Mantle]: 'https://1rpc.io/mantle',
      [MainnetChainIds.Base]: 'https://1rpc.io/base',
      [MainnetChainIds.Arbitrum]: 'https://arb1.arbitrum.io/rpc',
      [MainnetChainIds.Avalanche]: 'https://api.avax.network/ext/bc/C/rpc',
      [MainnetChainIds.Linea]: 'https://1rpc.io/linea',
      [MainnetChainIds.Scroll]: 'https://1rpc.io/scroll',
    }
  : {
      [TestnetChainIds.Goerli]: 'https://eth-goerli.public.blastapi.io',
      [TestnetChainIds.BSC]:
        'https://endpoints.omniatech.io/v1/bsc/testnet/public',
      [TestnetChainIds.zkSyncEraGoerli]: 'https://testnet.era.zksync.dev',
      [TestnetChainIds.Avalanche]: 'https://api.avax-test.network/ext/bc/C/rpc',
      [TestnetChainIds.PolygonMumbai]:
        'https://matic-mumbai.chainstacklabs.com',
      [TestnetChainIds.LineaGoerli]: 'https://rpc.goerli.linea.build',
      [TestnetChainIds.ArbitrumGoerli]:
        'https://endpoints.omniatech.io/v1/arbitrum/goerli/public',
      [TestnetChainIds.Optimism]:
        'https://endpoints.omniatech.io/v1/op/goerli/public',
      [TestnetChainIds.PolygonZkEVM]: 'https://rpc.public.zkevm-test.net',
      [TestnetChainIds.LightLinkPegasus]:
        'https://replicator.pegasus.lightlink.io/rpc/v1',
      [TestnetChainIds.X1]: 'https://x1testrpc.okx.com',
      [TestnetChainIds.TaikoKatlaL2]: 'https://rpc.katla.taiko.xyz',
      [TestnetChainIds.Scroll]: 'https://sepolia-rpc.scroll.io',
      [TestnetChainIds.opBNB]: 'https://opbnb-testnet-rpc.bnbchain.org',
      [TestnetChainIds.MantaPacific]:
        'https://manta-testnet.calderachain.xyz/http',
      [TestnetChainIds.Base]: 'https://base-goerli-rpc.publicnode.com',
      [TestnetChainIds.Metis]: 'https://goerli.gateway.metisdevops.link',
      [TestnetChainIds.Sepolia]: 'https://1rpc.io/sepolia',
    }

// ---------- The polling interval to fetch the balances of layer one ----------
export const REQUEST_ETHEREUM_BALANCES_INTERVAL = 5000 // TODO: revert to 20000

// ---------- Networks sort by layerOneChainId ----------
export const NETWORKS_SELECTION_PRIORITY: L1ChainId[] = [
  MainnetChainIds.Ethereum,
  MainnetChainIds.BSC,
  MainnetChainIds.Polygon,
  MainnetChainIds.Avalanche,
  MainnetChainIds.Arbitrum,
  MainnetChainIds.Optimism,
  MainnetChainIds.zkSyncEra,
  MainnetChainIds.Linea,
  MainnetChainIds.Scroll,
  MainnetChainIds.PolygonZkEVM,
  MainnetChainIds.Manta,
  MainnetChainIds.Mantle,
  MainnetChainIds.Metis,
  MainnetChainIds.Base,
  MainnetChainIds.opBNB,

  TestnetChainIds.Goerli,
  TestnetChainIds.zkSyncEraGoerli,
  TestnetChainIds.LineaGoerli,
  TestnetChainIds.Scroll,
  TestnetChainIds.PolygonZkEVM,
  TestnetChainIds.ArbitrumGoerli,
  TestnetChainIds.BSC,
]
